import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { cycleStatusLabel } from '../../notification/cycle-status.util';
import { OrgAlertHistoryItem } from '../patient.model';
import { FormUtils } from 'src/app/shared/utils/form-utils';

export const ALERT_HISTORY_EXPORT_ROW_CAP = 5000;

export type AlertHistoryExportRow = {
  campaign: string;
  created: string;
  status: string;
  subject: string;
  contactPhone: string;
  contactName: string;
  callbackLocation: string;
  alertId: string;
};

function mapAlertHistoryExportRows(items: OrgAlertHistoryItem[]): AlertHistoryExportRow[] {
  return items.map((item) => ({
    campaign: item.campaignTitle || 'N/A',
    created: new Date(item.initiatedOn).toLocaleString(),
    status: cycleStatusLabel(item.status),
    subject: item.patientName,
    contactPhone:
      item.contactPhone && item.contactPhone !== 'Unknown'
        ? FormUtils.formatPhoneNumber(item.contactPhone)
        : item.contactPhone,
    contactName: item.contactName,
    callbackLocation: item.callbackLocationName,
    alertId: item.id,
  }));
}

function labeledRows(rows: AlertHistoryExportRow[], subjectColumnLabel: string): Record<string, string>[] {
  return rows.map((row) => ({
    Campaign: row.campaign,
    Created: row.created,
    Status: row.status,
    [subjectColumnLabel]: row.subject,
    'Contact phone': row.contactPhone,
    'Contact name': row.contactName,
    'Callback location': row.callbackLocation,
    'Alert id': row.alertId,
  }));
}

export function downloadAlertHistoryCsv(
  items: OrgAlertHistoryItem[],
  subjectColumnLabel: string,
  fileName: string,
): void {
  const rows = labeledRows(mapAlertHistoryExportRows(items), subjectColumnLabel);
  const csv = Papa.unparse(rows);
  triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), fileName);
}

export function downloadAlertHistoryXlsx(
  items: OrgAlertHistoryItem[],
  subjectColumnLabel: string,
  fileName: string,
): void {
  const rows = labeledRows(mapAlertHistoryExportRows(items), subjectColumnLabel);
  const sheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Alert History');
  XLSX.writeFile(workbook, fileName);
}

export function alertHistoryExportFileName(
  orgDescription: string,
  dateRange?: { from: Date; to: Date },
  campaignTitle?: string,
): string {
  const slug = orgDescription
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  const range = dateRange
    ? `${formatFileDate(dateRange.from)}_${formatFileDate(dateRange.to)}`
    : slugifySegment(campaignTitle) || 'all-dates';
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `alert-history_${slug || 'org'}_${range}_${stamp}`;
}

function slugifySegment(value: string | undefined): string {
  return (
    value
      ?.trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) ?? ''
  );
}

function formatFileDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function localDayRangeMs(start: Date, end: Date): { dateFrom: number; dateTo: number } {
  const dateFrom = startOfLocalDay(start);
  const dateTo = endOfLocalDay(end);
  return { dateFrom, dateTo };
}

function startOfLocalDay(date: Date): number {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy.getTime();
}

function endOfLocalDay(date: Date): number {
  const copy = new Date(date);
  copy.setHours(23, 59, 59, 999);
  return copy.getTime();
}

export function historyPageOffsetToken(pageIndex: number, pageSize: number): string | undefined {
  if (pageIndex <= 0) {
    return undefined;
  }
  return btoa(String(pageIndex * pageSize));
}
